import {
  ComponentPropsWithoutRef,
  forwardRef,
  memo,
  useCallback,
  useState,
} from "react";

import {
  BACKGROUND_COLOR,
  TEXT_COLOR,
} from "@hl/base-components/lib/theme/colors";
import {
  FEATURE_FLAGS,
  useFeatureFlags,
} from "@hl/shared-features/lib/features/auth/hooks";
import {
  Button,
  Flex,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Text,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";

import { DraftProgress, GenerativeEditionType } from "apollo/graphql.generated";
import SaveDraftButton from "~/features/collections/forms/CreateCollectionMultistepForm/SaveDraftButton";
import useSaveDraft from "~/features/collections/forms/CreateCollectionMultistepForm/useSaveDraft";

import {
  CurateOutputControl,
  CurateOutputEditor,
  HashPreview,
  useHashList,
} from "../CodeGenerativeArt/CurateOutput";
import FormTitle from "../FormTitle";
import NextStepButton from "../NextStepButton";
import {
  useCreateCollectionFormContext,
  useCreateCollectionState,
} from "../context";

const DEFAULT_EDITION_SIZE = 100;

interface ItemProps extends ComponentPropsWithoutRef<"div"> {
  label: string;
  description: string;
}

const SelectItem = forwardRef<HTMLDivElement, ItemProps>(
  ({ label, description, ...others }: ItemProps, ref) => (
    <div ref={ref} {...others}>
      <Group noWrap>
        <div>
          <Text size="sm">{label}</Text>
          <Text size="xs" opacity={0.65}>
            {description}
          </Text>
        </div>
      </Group>
    </div>
  )
);

const SizeAndOutputForm = () => {
  const { dispatch } = useCreateCollectionState();
  const form = useCreateCollectionFormContext();
  const { hashList, handleAdd, handleDelete, handleClear, listHandlers } =
    useHashList();

  const { saveDraft, loading } = useSaveDraft({
    furthestStep: DraftProgress.ADDED_GEN_SIZE,
  });

  const onNextStep = () => {
    dispatch({
      type: "NEXT_STEP",
      payload: {
        form,
      },
    });
  };

  const enableCurateOutput = useFeatureFlags(
    FEATURE_FLAGS.ENABLE_GEN_SERIES_CURATED_OUTPUT
  );
  const [openedCurateOutput, curateOutputState] = useDisclosure(false);
  const [openedHashPreview, hashPreviewState] = useDisclosure(false);
  const [hashPreview, setHashPreview] = useState<string>();
  const isOpenEdition =
    form.values.generativeDetails?.editionType === GenerativeEditionType.Open;

  const [
    confirmSwitchToOpenOpened,
    { open: openConfirmSwitchToOpen, close: closeConfirmSwitchToOpen },
  ] = useDisclosure(false);

  const closeCurateOutput = useCallback(() => {
    curateOutputState.close();
    dispatch({
      type: "SET_FULL_WIDTH",
      payload: { enable: false },
    });
    form.validateField("collection.size");
  }, [curateOutputState]);

  const openCurateOutput = useCallback(() => {
    curateOutputState.open();
    dispatch({
      type: "SET_FULL_WIDTH",
      payload: { enable: true, showCloseButton: false },
    });
  }, [curateOutputState]);

  const closeHashPreview = useCallback(() => {
    hashPreviewState.close();
    dispatch({
      type: "SET_FULL_WIDTH",
      payload: { enable: false },
    });
    setHashPreview(undefined);
  }, [hashPreviewState]);

  const openHashPreview = useCallback(
    (hash: string) => {
      hashPreviewState.open();
      dispatch({
        type: "SET_FULL_WIDTH",
        payload: { enable: true, showCloseButton: false },
      });
      setHashPreview(hash);
    },
    [hashPreviewState]
  );

  if (openedHashPreview && hashPreview) {
    return (
      <HashPreview
        mediaUrl={form.values.metadata.mediaUrl || ""}
        hash={hashPreview}
        onClose={closeHashPreview}
      />
    );
  }

  if (openedCurateOutput) {
    return (
      <CurateOutputEditor
        onClose={closeCurateOutput}
        hashList={hashList}
        handleAdd={handleAdd}
        handleDelete={handleDelete}
        listHandlers={listHandlers}
      />
    );
  }

  return (
    <div>
      <FormTitle
        title="Size and output"
        buttons={[
          <SaveDraftButton
            key="draft"
            loading={loading}
            onClick={() => {
              saveDraft();
            }}
          />,
          <NextStepButton key="next" loading={loading} onClick={onNextStep} />,
        ]}
      />

      <Stack spacing={40}>
        <Select
          label="Series type"
          itemComponent={SelectItem}
          value={
            form.values.generativeDetails?.editionType ??
            GenerativeEditionType.Limited
          }
          onChange={(val: GenerativeEditionType) => {
            let switchEditionType = true;
            if (val === GenerativeEditionType.Open) {
              if (form.values.generativeDetails?.allowedHashes?.length) {
                // trigger modal to warn about selected hashes being lost
                openConfirmSwitchToOpen();
                switchEditionType = false;
              } else {
                form.setFieldValue("generativeDetails.curateOutput", false);
                form.setFieldValue("collection.size", 0);
              }
            } else {
              form.setFieldValue("collection.size", null);
            }

            if (switchEditionType)
              form.setFieldValue("generativeDetails.editionType", val);
          }}
          data={[
            {
              value: GenerativeEditionType.Limited,
              label: "Limited Series",
              description:
                "This collection will contain a finite number of tokens",
            },
            {
              value: GenerativeEditionType.Open,
              label: "Open Series",
              description:
                "People can mint an infinite number of tokens from this collection",
            },
          ]}
        />
        {!isOpenEdition && (
          <NumberInput
            {...form.getInputProps("collection.size")}
            label="Series size"
            placeholder={DEFAULT_EDITION_SIZE}
            min={1}
            rightSection={
              <Text size="sm" color={TEXT_COLOR.SECONDARY}>
                Items
              </Text>
            }
            rightSectionProps={{}}
            rightSectionWidth={100}
          />
        )}

        {enableCurateOutput && (
          <CurateOutputControl
            onOpenEditor={openCurateOutput}
            onOpenHashPreview={openHashPreview}
            disable={isOpenEdition}
            hashList={hashList}
            handleClear={handleClear}
            handleDelete={handleDelete}
          />
        )}
        <Modal
          opened={confirmSwitchToOpenOpened}
          onClose={closeConfirmSwitchToOpen}
          title="Switch to Open Edition"
        >
          <Text>
            Switching to an Open Series will discard the outputs you have
            selected. Are you sure you want to do this?
          </Text>
          <Flex justify="space-between" mt={16}>
            <Button
              onClick={closeConfirmSwitchToOpen}
              color={BACKGROUND_COLOR.SECONDARY}
              fullWidth
            >
              Cancel
            </Button>
            <Button
              ml={16}
              onClick={() => {
                form.setFieldValue(
                  "generativeDetails.editionType",
                  GenerativeEditionType.Open
                );
                form.setFieldValue("generativeDetails.curateOutput", false);
                form.setFieldValue("collection.size", 0);
                handleClear(); // clear selected hashes
                closeConfirmSwitchToOpen();
              }}
              fullWidth
            >
              Discard selected outputs
            </Button>
          </Flex>
        </Modal>
      </Stack>
      <NextStepButton
        fullWidth
        mt={48}
        size="xl"
        loading={loading}
        onClick={onNextStep}
      />
    </div>
  );
};

export default memo(SizeAndOutputForm);
